import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiX, FiAlertTriangle } from 'react-icons/fi';
import resourceAllocationService from '../../services/resourceAllocationService';
import projectService from '../../services/projectService';
import userService from '../../services/userService';
import squadService from '../../services/squadService';
import './ResourceAllocationForm.css';
import { toast } from 'react-toastify';

const ResourceAllocationForm = ({ allocationId = null, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [squads, setSquads] = useState([]);
  const [squadsLoading, setSquadsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [overAllocationWarning, setOverAllocationWarning] = useState(null);
  const [showCreateSquadModal, setShowCreateSquadModal] = useState(false);

  const [squadForm, setSquadForm] = useState({
    name: '',
    squad_code: '',
    project_id: '',
    status: 'Active'
  });
  const [formData, setFormData] = useState({
    project_id: '',
    squad_id: '',
    user_id: '',
    role: '',
    allocation_percentage: 100,
    start_date: '',
    end_date: '',
    billable_rate: '',
    notes: '',
    is_active: true
  });

  useEffect(() => {
    loadProjects();
    loadUsers();
    if (allocationId) {
      loadAllocation();
    }
  }, [allocationId]);

  useEffect(() => {
    if (formData.project_id) {
      loadSquads(formData.project_id);
    } else {
      setSquads([]);
    }
  }, [formData.project_id]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 100 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await userService.getForDropdown();
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]); // Set empty array on error
    }
  };

  const loadSquads = async (projectId) => {
    try {
      setSquadsLoading(true);
      const data = await squadService.getSquadsByProject(projectId);
      setSquads((data || []).filter((squad) => squad.status === "Active"));
    } catch (error) {
      console.error('Error loading squads:', error);
      setSquads([]);
    } finally {
      setSquadsLoading(false);
    }
  };

  const loadAllocation = async () => {
    try {
      setLoading(true);
      const data = await resourceAllocationService.getById(allocationId);
      setFormData({
        project_id: data.data.project_id || '',
        squad_id: data.data.squad_id || '',
        user_id: data.data.user_id || '',
        role: data.data.role || '',
        allocation_percentage: data.data.allocation_percentage || 100,
        start_date: data.data.start_date ? data.data.start_date.split('T')[0] : '',
        end_date: data.data.end_date ? data.data.end_date.split('T')[0] : '',
        billable_rate: data.data.billable_rate || '',
        notes: data.data.notes || '',
        is_active: data.data.is_active === true ? true : false,
      });

      if (data.data.project_id) {
        loadSquads(data.data.project_id);
      }
    } catch (error) {
      console.error('Error loading allocation:', error);
      toast.error('Failed to load allocation');
    } finally {
      setLoading(false);
    }
  };



  const checkOverAllocation = async () => {
    if (!formData.user_id || !formData.start_date) return;

    try {
      const params = {
        user_id: formData.user_id,
        start_date: formData.start_date,
        end_date: formData.end_date || formData.start_date
      };

      const data = await resourceAllocationService.getUtilization(params);
      const userStats = data.data.by_user?.find(u => u.user.id === formData.user_id);

      if (userStats) {
        const currentAllocation = allocationId ?
          userStats.allocations.find(a => a.id !== allocationId)?.allocation_percentage || 0 :
          userStats.utilization_percentage;

        const totalAllocation = currentAllocation + parseInt(formData.allocation_percentage);

        if (totalAllocation > 100) {
          setOverAllocationWarning({
            current: currentAllocation,
            new: totalAllocation,
            message: `This user will be ${totalAllocation}% allocated (over-allocated by ${totalAllocation - 100}%)`
          });
        } else {
          setOverAllocationWarning(null);
        }
      }
    } catch (error) {
      console.error('Error checking allocation:', error);
    }
  };

  const handleCreateSquad = async () => {
    // Validate squad name
    if (!squadForm.name.trim()) {
      toast.error('Squad name is required');
      return;
    }

    // Validate squad code
    if (!squadForm.squad_code.trim()) {
      toast.error('Squad code is required');
      return;
    }

    if (!formData.project_id) {
      toast.error('Please select a project first');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        project_id: formData.project_id,
        name: squadForm.name.trim(),
        squad_code: squadForm.squad_code.trim(),
        status: 'Active',
      };

      console.log('Create Squad Payload:', payload);

      const response = await squadService.createsquads(payload);

      toast.success(
        response?.message || 'Squad created successfully'
      );

      // Reload squads for selected project
      await loadSquads(formData.project_id);

      // Close modal
      setShowCreateSquadModal(false);

      // Reset squad form
      setSquadForm({
        name: '',
        squad_code: '',
      });

    } catch (err) {
      console.error('Error creating squad:', err);

      toast.error(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Failed to create squad'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      checkOverAllocation();
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.user_id, formData.allocation_percentage, formData.start_date, formData.end_date]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Create Squad selected
    if (name === 'squad_id' && value === 'create_squad') {
      setShowCreateSquadModal(true);

      // Keep squad_id empty until a squad is created
      setFormData(prev => ({
        ...prev,
        squad_id: '',
      }));

      // Clear squad validation error
      if (errors.squad_id) {
        setErrors(prev => ({
          ...prev,
          squad_id: '',
        }));
      }

      return;
    }

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value,
      };

      // When project changes, reset squad
      if (name === 'project_id') {
        updated.squad_id = '';
      }

      return updated;
    });

    // Clear validation error
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };
  const handleSubmit = async (e) => {
  e.preventDefault();

  const validation = resourceAllocationService.validateAllocationData(formData);

  if (!validation.isValid) {
    setErrors(validation.errors);
    return;
  }

  try {
    setLoading(true);

   const payload = {
      ...formData,
      is_active: formData.is_active
    };

    console.log('Resource Allocation Payload:', payload);

    if (allocationId) {
      await resourceAllocationService.update(allocationId, payload);
      toast.success('Allocation updated successfully');
    } else {
      await resourceAllocationService.create(payload);
      toast.success('Allocation created successfully');
    }

    if (onSuccess) {
      onSuccess();
    } else {
      navigate('/app/resources');
    }
  } catch (error) {
    console.error('Error saving allocation:', error);

    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error('Failed to save allocation');
    }
  } finally {
    setLoading(false);
  }
};


  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/app/resources');
    }
  };

  return (
    <div className="resource-allocation-form">
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label>Project *</label>
            <select
              name="project_id"
              value={formData.project_id}
              onChange={handleChange}
              className={errors.project_id ? 'error' : ''}
              required
            >
              <option value="">Select Project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            {errors.project_id && <span className="error-message">{errors.project_id}</span>}
          </div>

          <div className="form-group">
            <label>Squad *</label>
            <select
              name="squad_id"
              value={formData.squad_id}
              onChange={handleChange}
              className={errors.squad_id ? 'error' : ''}
              disabled={!formData.project_id || squadsLoading}
            >
              <option value="">
                {!formData.project_id
                  ? 'Select a project first'
                  : squadsLoading
                    ? 'Loading squads...'
                    : squads.length === 0
                      ? 'No squads found'
                      : 'Select Squad'}
              </option>

              <option value="create_squad">Create Squad</option>

              {squads.map(squad => (
                <option key={squad.id} value={squad.id}>
                  {squad.name}
                </option>
              ))}
            </select>
            {errors.squad_id && <span className="error-message">{errors.squad_id}</span>}
          </div>



          <div className="form-group">
            <label>User *</label>
            <select
              name="user_id"
              value={formData.user_id}
              onChange={handleChange}
              className={errors.user_id ? 'error' : ''}
              required
            >
              <option value="">Select User</option>
              {users.map(user => (
                <option key={user.value} value={user.value}>
                  {user.label} ({user.email})
                </option>
              ))}
            </select>
            {errors.user_id && <span className="error-message">{errors.user_id}</span>}
          </div>



          <div className="form-group">
            <label>Allocation Percentage * (0-100%)</label>
            <div className="allocation-input">
              <input
                type="number"
                name="allocation_percentage"
                min="0"
                max="100"
                value={formData.allocation_percentage}
                onChange={handleChange}
                className={`allocation-number ${errors.allocation_percentage ? 'error' : ''}`}
                required
              />
            </div>
            <div>
              <input
                type="range"
                name="allocation_percentage"
                min="0"
                max="100"
                step="5"
                value={formData.allocation_percentage}
                onChange={handleChange}
                className={errors.allocation_percentage ? 'error' : ''}
              />
              {/* <span className="percentage-label">%</span> */}
            </div>
            {errors.allocation_percentage && <span className="error-message">{errors.allocation_percentage}</span>}
          </div>

          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className={errors.start_date ? 'error' : ''}
              required
            />
            {errors.start_date && <span className="error-message">{errors.start_date}</span>}
          </div>

          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className={errors.end_date ? 'error' : ''}
            />
            {errors.end_date && <span className="error-message">{errors.end_date}</span>}
            <small className="help-text">Leave empty for ongoing allocation</small>
          </div>

          <div className="form-group">
            <label>Billable Rate (₹/hour)</label>
            <input
              type="number"
              name="billable_rate"
              value={formData.billable_rate}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Role</label>
            <input
              type="text"
              name="role"
              value={formData.role}
              onChange={handleChange}
              placeholder="e.g., Developer, Designer, QA"
            />
          </div>

          <div className="form-group full-width">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Additional notes about this allocation..."
            />
          </div>

          <div className="form-group">    
            <div className="flex items-center gap-6">
              <label className="!flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="is_active"
                  checked={formData.is_active === true}
                  onChange={() =>
                    setFormData(prev => ({
                      ...prev,
                      is_active: true
                    }))
                  }
                />
                <span>Active</span>
              </label>

              <label className="!flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="is_active"
                  checked={formData.is_active === false}
                  onChange={() =>
                    setFormData(prev => ({
                      ...prev,
                      is_active: false
                    }))
                  }
                />
                <span>In&nbsp;Active</span>
              </label>
            </div>
          </div>

        </div>

        {overAllocationWarning && (
          <div className="warning-banner">
            <FiAlertTriangle />
            <div>
              <strong>Over-allocation Warning</strong>
              <p>{overAllocationWarning.message}</p>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
            <FiX /> Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            <FiSave /> {loading ? 'Saving...' : allocationId ? 'Update Allocation' : 'Create Allocation'}
          </button>
        </div>


        {showCreateSquadModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
            <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">

              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">
                    Create Squad
                  </h2>

                  <p className="mt-1 text-sm text-gray-500">
                    Create a new squad for this project
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowCreateSquadModal(false);

                    setSquadForm({
                      name: '',
                      description: '',
                    });
                  }}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div className="space-y-5 px-6 py-6">

                {/* Squad Name */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Squad Name <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="text"
                    value={squadForm.name}
                    onChange={(e) =>
                      setSquadForm(prev => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Enter squad name"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Squad Code <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="text"
                    value={squadForm.squad_code}
                    onChange={(e) =>
                      setSquadForm(prev => ({
                        ...prev,
                        squad_code: e.target.value,
                      }))
                    }
                    placeholder="Enter squad name"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>


              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">

                <button
                  type="button"
                  onClick={() => {
                    setShowCreateSquadModal(false);

                    setSquadForm({
                      name: '',
                      description: '',
                    });
                  }}
                  className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleCreateSquad}
                  disabled={loading}
                  className={`rounded-lg px-5 py-2.5 text-sm font-medium text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-indigo-200 ${loading
                    ? 'cursor-not-allowed bg-indigo-400'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                >
                  {loading ? 'Creating...' : 'Create Squad'}
                </button>

              </div>

            </div>
          </div>
        )}


      </form>
    </div>
  );
};

export default ResourceAllocationForm;

